import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, FileText, Edit } from 'lucide-react';

interface VersioningWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: (reason?: string, description?: string) => void;
  warningType: 'new_version' | 'change_order';
  message: string;
  isLoading?: boolean;
}

const VersioningWarningDialog: React.FC<VersioningWarningDialogProps> = ({
  isOpen,
  onClose,
  onProceed,
  warningType,
  message,
  isLoading = false
}) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');

  const handleProceed = () => {
    onProceed(reason || undefined, description || undefined);
  };

  const getIcon = () => {
    if (warningType === 'change_order') {
      return <Edit className="h-6 w-6 text-orange-500" />;
    }
    return <FileText className="h-6 w-6 text-blue-500" />;
  };

  const getTitle = () => {
    if (warningType === 'change_order') {
      return 'Create Change Order';
    }
    return 'Create New Version';
  };

  const getActionText = () => {
    if (warningType === 'change_order') {
      return isLoading ? 'Creating Change Order...' : 'Create Change Order';
    }
    return isLoading ? 'Creating Version...' : 'Create New Version';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {getIcon()}
            <div>
              <DialogTitle>{getTitle()}</DialogTitle>
              <DialogDescription className="mt-1">
                {message}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason for {warningType === 'change_order' ? 'Change Order' : 'New Version'} (Optional)
            </Label>
            <Textarea
              id="reason"
              placeholder="e.g., Client requested additional features, Updated pricing, Scope changes..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description of Changes (Optional)
            </Label>
            <Textarea
              id="description"
              placeholder="Describe what has changed in this version..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            disabled={isLoading}
            variant={warningType === 'change_order' ? 'default' : 'default'}
          >
            {getActionText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VersioningWarningDialog;